import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowLeft,
  EllipsisVertical,
  Pencil,
  Plus,
  Send,
  Users,
} from "lucide-react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import sh from "bundles/common/utils/sh";
import ProfileDetailPanel, { getAvatarColors, getOutcomeBadge } from "./common/ProfileDetailPanel";
import { useCandidateModal } from "./common/CandidateModalProvider";
import CreateProfileDrawer from "./common/CreateProfileDrawer";
import ConfirmDialog from "./common/ConfirmDialog";
import ApplyToOfferDialog from "./common/ApplyToOfferDialog";
import AssignUsersDrawer from "./common/AssignUsersDrawer";
import CreatePipelineDrawer from "./dashboard/CreatePipelineDrawer";
import UserAvatar from "./common/UserAvatar";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";


const EXTERNAL_PROFILE_CREATED_EVENT = "external-profile:created";

const InternalProfilePipelinePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);

  const [loading, setLoading] = useState(true);
  const [savingBrief, setSavingBrief] = useState(false);
  const [error, setError] = useState("");
  const [pipeline, setPipeline] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [activeTab, setActiveTab] = useState("profiles");
  const [brief, setBrief] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { openCreateCandidate, updateCandidateModal } = useCandidateModal();
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  const [editPipelineOpen, setEditPipelineOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [applyAllConfirmOpen, setApplyAllConfirmOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const fetchPipeline = useCallback(() => {
    if (!id) return;

    setLoading(true);
    setError("");

    sh.get(`/internal/profile_pipelines/${id}`)
      .then((res) => {
        const data = res.data || {};
        const pipelineProfiles = data.external_profiles || [];
        setPipeline(data);
        setBrief(data.content || "");
        setProfiles(pipelineProfiles);
        setSelectedProfileId((prev) => prev || pipelineProfiles[0]?.id || null);
      })
      .catch(() => {
        setError("Failed to load pipeline.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  useEffect(() => {
    const handler = () => fetchPipeline();
    window.addEventListener(EXTERNAL_PROFILE_CREATED_EVENT, handler);
    return () => window.removeEventListener(EXTERNAL_PROFILE_CREATED_EVENT, handler);
  }, [fetchPipeline]);

  const selectedProfile = useMemo(() => {
    return profiles.find((profile) => String(profile.id) === String(selectedProfileId));
  }, [profiles, selectedProfileId]);


  const handleLogOutcome = useCallback((outcome, time, notes, followUpAt) => {
    if (!selectedProfile) return;

    const callLogPayload = { outcome, profile_pipeline_id: id };

    if (followUpAt) {
      callLogPayload.follow_up_at = followUpAt;
    } else if (time && outcome === "follow_up_today") {
      const [h, m] = time.split(":");
      const date = new Date();
      date.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
      callLogPayload.follow_up_at = date.toISOString();
    }

    if (notes) callLogPayload.notes = notes;

    sh.post(`/internal/external_profiles/${selectedProfile.id}/call_logs`, {
      authenticity_token,
      call_log: callLogPayload,
    })
      .then((res) => {
        const profile = res?.data?.profile;
        if (!profile) return;
        setProfiles((prev) => prev.map((item) => (String(item.id) === String(profile.id) ? profile : item)));
      })
      .catch(() => {
        setError("Could not log outcome.");
      });
  }, [authenticity_token, selectedProfile]);

  const handleProfileCreated = useCallback((createdProfile) => {
    setProfiles((prev) => {
      const existingIndex = prev.findIndex((item) => String(item.id) === String(createdProfile.id));
      if (existingIndex === -1) return [createdProfile, ...prev];
      const next = [...prev];
      next[existingIndex] = { ...next[existingIndex], ...createdProfile };
      return next;
    });
    setSelectedProfileId(createdProfile.id);
  }, []);


  const saveBrief = useCallback(() => {
    if (!id || savingBrief) return;
    setSavingBrief(true);

    sh.put(`/internal/profile_pipelines/${id}`, {
      authenticity_token,
      profile_pipeline: { content: brief },
    })
      .then((res) => {
        setPipeline((prev) => ({ ...prev, ...(res.data || {}) }));
      })
      .catch(() => {
        setError("Failed to save brief.");
      })
      .finally(() => setSavingBrief(false));
  }, [authenticity_token, brief, id, savingBrief]);

  const handleCreateCandidate = useCallback((values, { reset, resumeFile, autoEmail, close } = {}) => {
    const appendValue = appendFormValue;

    const externalProfileId = selectedProfile?.id;

    const applyProfileLink = (candidate) => {
      if (!externalProfileId || !candidate?.id) return;
      setProfiles((prev) =>
        prev.map((l) =>
          String(l.id) === String(externalProfileId) ? { ...l, candidate_id: candidate.id } : l
        )
      );
    };

    const showCreateWarnings = (candidate) => {
      const warnings = candidate?.warnings;
      if (Array.isArray(warnings) && warnings.length > 0) {
        warnings.forEach((message) => enqueueSnackbar(message, { variant: "warning" }));
      }
    };

    const doPost = (payload) =>
      sh.post("/internal/candidates", payload)
        .then((res) => {
          const candidate = res.data;
          applyProfileLink(candidate);
          showCreateWarnings(candidate);
          return candidate;
        })
        .then(() => {
          enqueueSnackbar("Candidate created", { variant: "success" });
          reset?.();
          close?.();
        })
        .catch((err) => {
          const messages = err?.response?.data?.errors;
          if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
            messages.forEach((message) => enqueueSnackbar(message, { variant: "error" }));
          } else {
            enqueueSnackbar("Failed to create candidate.", { variant: "error" });
          }
        });

    if (!resumeFile) {
      return doPost({
        candidate: values,
        authenticity_token,
        auto_email: autoEmail,
        ...(externalProfileId ? { external_profile_id: externalProfileId } : {}),
      });
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);
    Object.entries(values || {}).forEach(([k, v]) => appendValue(formData, `candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);
    if (autoEmail && typeof autoEmail === "object") {
      Object.entries(autoEmail).forEach(([k, v]) => appendValue(formData, `auto_email[${k}]`, v));
    }
    if (externalProfileId) {
      formData.append("external_profile_id", String(externalProfileId));
    }
    return doPost(formData);
  }, [authenticity_token, enqueueSnackbar, selectedProfile]);

  const handleSignUpToCandidate = useCallback((profile) => {
    openCreateCandidate({
      onSubmit: handleCreateCandidate,
      defaultValues: {
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        region: profile.region || "",
        desired_job_title: profile.desired_title || "",
      },
    });

    // Fetch the profile's CV as a File so it pre-fills the resume field
    if (profile.cv_url) {
      fetch(profile.cv_url)
        .then((res) => res.blob())
        .then((blob) => {
          const name = profile.cv_url.split("/").pop()?.split("?")[0] || "cv.pdf";
          updateCandidateModal({ defaultResumeFile: new File([blob], name, { type: blob.type }) });
        });
    }
  }, [openCreateCandidate, updateCandidateModal, handleCreateCandidate]);

  const handleEdit = useCallback((profile) => {
    setEditingProfile(profile);
    setEditProfileOpen(true);
  }, []);

  const handleToggleJobOfferField = useCallback((pipelineJobOfferId, field, value) => {
    if (!pipelineJobOfferId) return;

    setPipeline((prev) => ({
      ...prev,
      job_offers: (prev.job_offers || []).map((jo) =>
        jo.pipeline_job_offer_id === pipelineJobOfferId ? { ...jo, [field]: value } : jo
      ),
    }));

    const label = field === "urgent" ? "Urgent" : "On hold";

    sh.patch(`/internal/pipeline_job_offers/${pipelineJobOfferId}`, {
      [field]: value,
      authenticity_token,
    }).then(() => {
      enqueueSnackbar(`${label} ${value ? "enabled" : "disabled"}.`, { variant: "success" });
    }).catch(() => {
      setPipeline((prev) => ({
        ...prev,
        job_offers: (prev.job_offers || []).map((jo) =>
          jo.pipeline_job_offer_id === pipelineJobOfferId ? { ...jo, [field]: !value } : jo
        ),
      }));
      enqueueSnackbar("Failed to update.", { variant: "error" });
    });
  }, [authenticity_token, enqueueSnackbar]);

  const handleUpdated = useCallback((updatedProfile) => {
    setProfiles((prev) => prev.map((p) => (String(p.id) === String(updatedProfile.id) ? { ...p, ...updatedProfile } : p)));
  }, []);

  const handleArchiveProfile = useCallback((profileId) => {
    if (!profileId || !id) return;

    sh.post(`/internal/profile_pipelines/${id}/external_profiles/${profileId}/archive`, {
      authenticity_token,
    })
      .then(() => {
        setArchiveConfirmId(null);
        setProfiles((prev) => {
          const next = prev.filter((p) => String(p.id) !== String(profileId));
          if (String(selectedProfileId) === String(profileId)) {
            setSelectedProfileId(next[0]?.id || null);
          }
          return next;
        });
      })
      .catch(() => {
        setError("Failed to archive profile.");
      });
  }, [authenticity_token, id, selectedProfileId]);

  const handleApply = useCallback((jobOfferIds) => {
    if (!selectedProfile?.candidate_id || !id || applying) return;

    setApplying(true);
    sh.post(`/internal/candidates/${selectedProfile.candidate_id}/job_applications`, {
      authenticity_token,
      job_offer_ids: jobOfferIds,
      cover_letter: "Sent by talent team",
      utm_source: "internal",
    })
      .then((res) => {
        const { applied, skipped } = res.data;
        if (applied.length > 0) {
          enqueueSnackbar(`Applied to ${applied.length} offer${applied.length > 1 ? "s" : ""}`, { variant: "success" });
          setProfiles((prev) => prev.map((p) => {
            if (String(p.id) !== String(selectedProfile.id)) return p;
            return { ...p, applied_offer_ids: [...(p.applied_offer_ids || []), ...applied] };
          }));
        }
        if (skipped.length > 0) enqueueSnackbar(`${skipped.length} already applied — skipped`, { variant: "info" });
        setApplyAllConfirmOpen(false);
      })
      .catch(() => enqueueSnackbar("Failed to apply", { variant: "error" }))
      .finally(() => setApplying(false));
  }, [authenticity_token, applying, enqueueSnackbar, id, selectedProfile]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!pipeline) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Pipeline not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "hsl(140 10% 95%)" }}>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", width: "100%", minHeight: "100vh" }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          sx={{ px: 3, pt: 2.5, pb: 0, borderBottom: "1px solid hsl(140 8% 88%)", backgroundColor: "#fff" }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <IconButton size="small" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/pipelines"))} sx={{ mt: 0.2 }}>
                <ArrowLeft size={18} />
              </IconButton>
              <Box>
                <Typography sx={{ fontSize: 18, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: "28px" }}>
                  {pipeline.title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                  <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>
                    {profiles.length} profiles
                  </Typography>
                  {pipeline.assigned_users?.length > 0 && (
                    <>
                      <Box sx={{ width: 3, height: 3, borderRadius: "50%", backgroundColor: "hsl(150 10% 46%)" }} />
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {pipeline.assigned_users.slice(0, 4).map((u) => (
                          <UserAvatar
                            key={u.id}
                            user={u}
                            size={22}
                            sx={{ border: "2px solid #fff", ml: "-6px", "&:first-of-type": { ml: 0 } }}
                          />
                        ))}
                        {pipeline.assigned_users.length > 4 && (
                          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", ml: 0.5 }}>
                            +{pipeline.assigned_users.length - 4}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Users size={14} />}
                onClick={() => setAssignDrawerOpen(true)}
                disableRipple
                sx={{
                  textTransform: "none",
                  height: 36,
                  px: 1.5,
                  borderRadius: "calc(-2px + 0.75rem)",
                  borderColor: "hsl(140 8% 88%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  color: "hsl(150 30% 12%)",
                  fontSize: 14,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  "& .MuiButton-startIcon": { mr: 0.75 },
                  "&:hover": {
                    borderColor: "hsl(140 8% 88%)",
                    backgroundColor: "rgba(0,0,0,0.04)",
                    color: "hsl(150 30% 12%)",
                  },
                }}
              >
                Manage access
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Plus size={16} />}
                onClick={() => setDrawerOpen(true)}
                sx={{
                  textTransform: "none",
                  height: 36,
                  px: 1.5,
                  borderRadius: "calc(-2px + 0.75rem)",
                  borderColor: "hsl(140 8% 88%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  color: "hsl(150 30% 12%)",
                  fontSize: 14,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  "& .MuiButton-startIcon": { mr: 0.75 },
                  "&:hover": {
                    borderColor: "hsl(140 8% 88%)",
                    backgroundColor: "rgb(177, 252, 29)",
                    color: "hsl(150 30% 12%)",
                  },
                }}
              >
                Add profile
              </Button>

              <IconButton
                size="small"
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                disableRipple
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "calc(-2px + 0.75rem)",
                  border: "1px solid hsl(140 8% 88%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  color: "hsl(150 10% 46%)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
              >
                <EllipsisVertical size={16} />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: "8px",
                      border: "1px solid hsl(140 8% 88%)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      minWidth: 160,
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchorEl(null);
                    setEditPipelineOpen(true);
                  }}
                  sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Pencil size={14} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" } }}>
                    Edit pipeline
                  </ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3, mt: "0.75rem", mb: "-1px" }}>
            {["profiles", "job offers", "brief"].map((tab) => {
              const active = activeTab === tab;
              return (
                <Button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  disableRipple
                  sx={{
                    p: 0,
                    minWidth: 0,
                    minHeight: 0,
                    height: "auto",
                    borderRadius: 0,
                    textTransform: "capitalize",
                    pb: 1,
                    borderBottom: "3px solid",
                    borderColor: active ? "hsl(150 30% 12%)" : "transparent",
                    color: active ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                    fontSize: 14,
                    fontFamily: active ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                    lineHeight: 1.2,
                    transition: "color 0.2s ease",
                    "&:hover": {
                      borderColor: active ? "hsl(150 30% 12%)" : "transparent",
                      color: "hsl(150 30% 12%)",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {tab}
                </Button>
              );
            })}
          </Box>
        </Box>

        {activeTab === "profiles" ? (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.08 }}
            sx={{ flex: 1, display: "flex", overflow: "hidden", border: "1px solid hsl(140 8% 88%)", borderTop: "none", backgroundColor: "#fff" }}
          >
            <Box sx={{ width: 260, flexShrink: 0, borderRight: "1px solid hsl(140 8% 88%)", overflowY: "auto" }}>
              {profiles.length === 0 ? (
                <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", textAlign: "center", py: 5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>No profiles yet</Typography>
              ) : (
                profiles.map((profile) => {
                  const isSelected = String(profile.id) === String(selectedProfileId);
                  const badge = getOutcomeBadge(profile.last_action);
                  const colors = getAvatarColors(profile.id);

                  return (
                    <Button
                      key={profile.id}
                      disableRipple
                      onClick={() => setSelectedProfileId(profile.id)}
                      sx={{
                        borderRadius: 0,
                        borderBottom: "1px solid hsl(140 8% 88%)",
                        px: "1rem",
                        py: "0.75rem",
                        width: "100%",
                        height: "auto",
                        justifyContent: "flex-start",
                        gap: 1.5,
                        textTransform: "none",
                        borderLeft: isSelected ? "2px solid hsl(217 91% 55%)" : "2px solid transparent",
                        backgroundColor: isSelected ? "hsla(217 91% 95% / .3)" : "transparent",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", bgcolor: colors.bg, color: colors.text }}>
                        {(profile.first_name || "")[0]}{(profile.last_name || "")[0]}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <Typography sx={{ fontSize: 14, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }} noWrap>
                          {[profile.first_name, profile.last_name].filter(Boolean).join(" ")}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }} noWrap>
                          {profile.desired_title || "No desired title"}
                        </Typography>
                      </Box>
                      <Chip
                        label={badge.label}
                        size="small"
                        sx={{
                          height: 20,
                          borderRadius: "999px",
                          fontSize: 10,
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          backgroundColor: badge.bg,
                          color: badge.text,
                        }}
                      />
                    </Button>
                  );
                })
              )}
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", p: 3, bgcolor: "hsl(140 10% 95%)", "--input-bg": "white" }}>
              {selectedProfile ? (
                <>
                  <ProfileDetailPanel
                    profile={selectedProfile}
                    onLogOutcome={handleLogOutcome}
                    onSignUpToCandidate={handleSignUpToCandidate}
                    onArchive={(profileId) => setArchiveConfirmId(profileId)}
                    onEdit={handleEdit}
                    initialOfferIds={(pipeline?.job_offers || []).map((jo) => jo.id)}
                    onApplied={() => {
                      sh.get(`/internal/profile_pipelines/${id}`).then((res) => {
                        const data = res.data || {};
                        setProfiles(data.external_profiles || []);
                      }).catch(() => {});
                    }}
                    hideApplyButton
                  />

                  {selectedProfile?.candidate_id && (
                    <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                      {pipeline?.job_offers?.length > 0 && (
                        <Button
                          variant="contained"
                          disableElevation
                          disableRipple
                          disabled={applying}
                          onClick={() => setApplyAllConfirmOpen(true)}
                          startIcon={<Send size={14} />}
                          sx={{
                            flex: 1,
                            height: 36,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontSize: 13,
                            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                            backgroundColor: "hsl(150 38% 17%)",
                            color: "hsl(80 97% 72%)",
                            "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)" },
                            "&.Mui-disabled": { opacity: 0.4, backgroundColor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)", pointerEvents: "none" },
                          }}
                        >
                          Apply to all offers
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        disableElevation
                        disableRipple
                        onClick={() => setApplyOpen(true)}
                        startIcon={<Send size={14} />}
                        sx={{
                          flex: 1,
                          height: 36,
                          borderRadius: "12px",
                          textTransform: "none",
                          fontSize: 13,
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          borderColor: "hsl(140 8% 88%)",
                          color: "hsl(150 30% 12%)",
                          backgroundColor: "#fff",
                          "&:hover": { borderColor: "hsl(140 8% 88%)", backgroundColor: "rgba(0,0,0,0.04)" },
                        }}
                      >
                        Apply to an offer
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", textAlign: "center", py: 10 }}>Select a profile to view details</Typography>
              )}
            </Box>
          </Box>
        ) : activeTab === "job offers" ? (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.08 }}
            sx={{ flex: 1, p: 3 }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid hsl(140 8% 88%)",
                backgroundColor: "#fff",
                overflow: "hidden",
              }}
            >
              {(pipeline.job_offers || []).length === 0 ? (
                <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", textAlign: "center", py: 5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  No job offers linked to this pipeline.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Company", "Job Offer", "Location", "Sent from Talent Team", "End of Mandate", "Urgent", "On Hold"].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            backgroundColor: "#F9FAFF",
                            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                            fontSize: 12,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            color: "#2A334E",
                            borderBottom: "2px solid rgba(42,51,78,0.1)",
                            py: 1.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(pipeline.job_offers || []).map((jo) => (
                      <TableRow
                        key={jo.id}
                        hover
                        onClick={() => navigate(`/internal/job_offers/${jo.id}`)}
                        sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "#2A334E", py: 1.5 }}>
                          {jo.company_id ? (
                            <Box
                              component="span"
                              onClick={(e) => { e.stopPropagation(); navigate(`/internal/companies/${jo.company_id}`); }}
                              sx={{ color: "#079344", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                            >
                              {jo.company_name}
                            </Box>
                          ) : (jo.company_name || "—")}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#2A334E", py: 1.5 }}>
                          {jo.title || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "#2A334E", py: 1.5 }}>
                          {jo.location || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "#2A334E", py: 1.5, textAlign: "center" }}>
                          {jo.candidates_sent ?? 0}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "#2A334E", py: 1.5 }}>
                          {jo.end_of_mandate ? new Intl.DateTimeFormat("fr-BE", { year: "2-digit", month: "2-digit", day: "2-digit" }).format(new Date(jo.end_of_mandate)) : "—"}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={!!jo.urgent}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleToggleJobOfferField(jo.pipeline_job_offer_id, "urgent", e.target.checked)}
                            sx={{ p: 0.5, color: "rgba(42,51,78,0.3)", "&.Mui-checked": { color: "#D32F2F" } }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={!!jo.on_hold}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleToggleJobOfferField(jo.pipeline_job_offer_id, "on_hold", e.target.checked)}
                            sx={{ p: 0.5, color: "rgba(42,51,78,0.3)", "&.Mui-checked": { color: "#F57C00" } }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        ) : (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.08 }}
            sx={{ flex: 1, p: 3 }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid hsl(140 8% 88%)",
                backgroundColor: "#fff",
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "hsl(150 10% 46%)",
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  mb: 1.5,
                }}
              >
                Job requirements & brief
              </Typography>
              <Box
                component="textarea"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="Write what the company is looking for — requirements, salary range, culture fit, must-haves, nice-to-haves..."
                sx={{
                  flex: 1,
                  bgcolor: "hsl(140 10% 95%)",
                  minHeight: 300,
                  width: "100%",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  p: 1,
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                  "&::placeholder": { color: "hsl(150 10% 46%)" },
                  "&:focus": { outline: "none" },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={saveBrief}
                  disabled={savingBrief}
                  sx={{
                    height: 36,
                    px: 1.5,
                    borderRadius: "6px",
                    textTransform: "none",
                    fontSize: 14,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    backgroundColor: "hsl(150 38% 17%)",
                    color: "hsl(80 97% 72%)",
                    "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)" },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                      backgroundColor: "hsl(150 38% 17%)",
                      color: "hsl(80 97% 72%)",
                      pointerEvents: "none",
                    },
                  }}
                >
                  {savingBrief ? "Saving..." : "Save brief"}
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      <CreateProfileDrawer
        open={drawerOpen || editProfileOpen}
        onClose={() => { setDrawerOpen(false); setEditProfileOpen(false); }}
        pipelineId={id}
        onCreated={handleProfileCreated}
        editingProfile={editProfileOpen ? editingProfile : undefined}
        onUpdated={handleUpdated}
      />

      <ConfirmDialog
        open={Boolean(archiveConfirmId)}
        onClose={() => setArchiveConfirmId(null)}
        onConfirm={() => handleArchiveProfile(archiveConfirmId)}
        title="Archive profile?"
        description="This profile will be removed from the pipeline. This action cannot be undone."
        confirmLabel="Archive"
        destructive
      />

      <AssignUsersDrawer
        open={assignDrawerOpen}
        onClose={() => setAssignDrawerOpen(false)}
        pipelineId={id}
        assignedUserIds={pipeline.assigned_internal_user_ids || []}
        ownerId={pipeline.owner?.id}
        onUpdated={(updated) => setPipeline((prev) => ({ ...prev, ...updated }))}
      />

      <CreatePipelineDrawer
        open={editPipelineOpen}
        onClose={() => setEditPipelineOpen(false)}
        editingPipeline={pipeline}
        onUpdated={(updated) => {
          setPipeline((prev) => ({ ...prev, ...updated }));
          setEditPipelineOpen(false);
        }}
      />

      {/* Apply to all confirmation */}
      <ConfirmDialog
        open={applyAllConfirmOpen}
        onClose={() => setApplyAllConfirmOpen(false)}
        onConfirm={() => handleApply(pipeline.job_offers.map((jo) => jo.id))}
        title="Apply to all linked offers?"
        description={`This will submit ${selectedProfile ? [selectedProfile.first_name, selectedProfile.last_name].filter(Boolean).join(" ") : "this candidate"} to ${pipeline?.job_offers?.length || 0} job offer${(pipeline?.job_offers?.length || 0) > 1 ? "s" : ""} with "Sent by talent team" as the cover letter.`}
        confirmLabel={applying ? "Applying..." : "Apply"}
      />

      {/* Apply to offer dialog */}
      {selectedProfile?.candidate_id && (
        <ApplyToOfferDialog
          open={applyOpen}
          onClose={() => setApplyOpen(false)}
          candidateId={selectedProfile.candidate_id}
          candidateResumeUrl={selectedProfile.cv_url || selectedProfile.candidate_resume_url}
          appliedOfferIds={selectedProfile?.applied_offer_ids || []}
          initialOfferIds={(pipeline?.job_offers || []).map((jo) => jo.id)}
          onApplied={() => {
            sh.get(`/internal/profile_pipelines/${id}`).then((res) => {
              const data = res.data || {};
              setProfiles(data.external_profiles || []);
            }).catch(() => {});
          }}
        />
      )}
    </Box>
  );
};

export default InternalProfilePipelinePage;
